import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  Directory,
  File,
  FileBrowserStorageObjectFragment,
  useGetDataRoomsQuery,
} from '../../graphql/generated';
import { isDirectory, isFile } from '../../graphql/utils/utils';
import { getAvailableDataRooms } from '../../utils/treaty-utils/get-available-data-rooms';
import {
  AutoCompleteMenuItem,
  AutoCompleteMenuStaticItem,
  SearchResultItem,
} from '../autocomplete-input/autocomplete-input';
import { generateDirectoryLink, generateFileLink } from '../file-browser/name';

const createFileItem = (file: File): SearchResultItem => ({
  type: 'FILE',
  title: file.name,
  subtitle: file.cedant + '/' + file.path,
  value: generateFileLink(file),
});

const createDirectoryItem = (directory: Directory): SearchResultItem => ({
  type: 'DIRECTORY',
  title: directory.name,
  subtitle: directory.cedant + '/' + directory.path,
  value: generateDirectoryLink(directory),
});

export const createFullSearchDeeplink = (): AutoCompleteMenuStaticItem => ({
  type: 'FULL_SEARCH_DEEPLINK',
});

export const formatSearchResults = (
  results?: FileBrowserStorageObjectFragment[]
): AutoCompleteMenuItem[] => {
  if (!results) return [];

  const formattedResults = results.reduce<AutoCompleteMenuItem[]>(
    (acc, value) => {
      if (isFile(value)) {
        acc.push(createFileItem(value));
      }

      if (isDirectory(value)) {
        acc.push(createDirectoryItem(value));
      }

      return acc;
    },
    []
  );

  return formattedResults.concat(createFullSearchDeeplink());
};

export const useGetCedantContextForSearch = () => {
  const params = useParams();
  const { data: treatyData } = useGetDataRoomsQuery({
    fetchPolicy: 'cache-first',
    skip: !!params.cedantId,
  });

  return useMemo(() => {
    if (params.cedantId) {
      return [params.cedantId];
    }

    return getAvailableDataRooms(treatyData).map(({ cedant }) => cedant) || [];
  }, [params.cedantId, treatyData]);
};
